import React, { useEffect, useRef } from 'react';

import { Formik, useField } from 'formik';
import * as Yup from 'yup';

import { formatRate, isRate } from './fxFormat';
import { CheckFigure, RateInput } from './FxRatesTable';

import { toDateString, toLocalDate } from 'Helpers/dateFormat';
import { modalOptions } from 'Helpers/modalOptions';

import { FormikDatePickerField } from 'CommonComponents/FormikDatePickerField';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextFormField from 'CommonComponents/TextFormField';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

const LockSchema = Yup.object().shape({
    rate: Yup.string()
        .required($t('Enter a rate above zero, with up to 6 decimal places'))
        .test('rate', $t('Enter a rate above zero, with up to 6 decimal places'), (value) => isRate(value)),
    as_at: Yup.date().typeError($t('Choose an as-at date')).required($t('As at date is required')),
    reason: Yup.string().trim().required($t('Reason is required')),
});

/** The dialog's rate: four places until focused, the full rate to edit. */
function LockRateField({ label }) {
    const [field, meta, helpers] = useField('rate');

    return (
        <div className="tw-flex tw-flex-col tw-space-y-1.5">
            <label htmlFor="fx-lock-rate" className="text-input__label">
                {label} *
            </label>
            <RateInput
                id="fx-lock-rate"
                name="rate"
                className="text-input"
                value={field.value}
                invalid={meta.touched && !!meta.error}
                onChange={(value) => helpers.setValue(value)}
                onBlur={() => helpers.setTouched(true)}
            />
            {meta.touched && meta.error ? <div className="tw-text-red-500 tw-text-sm">{meta.error}</div> : null}
        </div>
    );
}

/**
 * Locks a project at its own rate against the anchor, or edits the lock.
 * Rate, as-at date and reason are all required. The rate is prefilled as
 * stored, to six places, so saving an edit unchanged keeps it exactly.
 */
export default function FxLockModal({ project, anchor, lockUrl, onSaved, onClose }) {
    const modal = useModal({ options: { ...modalOptions(false), onHide: onClose }, visible: true });
    const toast = useToast();

    const modalStateRef = useRef(modal);
    modalStateRef.current = modal;

    useEffect(
        () => () => {
            const { element } = modalStateRef.current;

            if (element && element.isVisible()) {
                element.hide();
            }
        },
        [],
    );

    const close = () => modal.hide();
    const isEdit = project.mode === 'locked';

    const initialValues = {
        rate: (isEdit ? project.rate : project.common_rate) ?? '',
        as_at: isEdit ? toLocalDate(project.as_at) : '',
        reason: isEdit ? project.reason || '' : '',
    };

    return (
        <Modal id="fx-lock-modal" modal={modal}>
            <Formik
                initialValues={initialValues}
                validationSchema={LockSchema}
                onSubmit={(values, actions) => {
                    // Not returned: Formik would reset the form state once the promise settles,
                    // after a successful save has already closed and unmounted it.
                    axios
                        .put(lockUrl, { rate: String(values.rate).trim(), as_at: toDateString(values.as_at), reason: values.reason.trim() })
                        .then(({ data }) => {
                            toast.success($t('{{project}} locked at {{rate}}', { project: project.name, rate: formatRate(values.rate) }));
                            onSaved(data);
                            close();
                        })
                        .catch((e) => {
                            actions.setSubmitting(false);

                            if (e.response?.data?.errors) {
                                actions.setErrors(e.response.data.errors);
                                if (e.response.data.errors.event) {
                                    toast.error(e.response.data.errors.event[0]);
                                }
                            } else {
                                toast.error($t('Something went wrong'));
                            }
                        });
                }}
            >
                {({ values, errors, touched, isSubmitting, handleSubmit }) => (
                    <>
                        <ModalHeader
                            title={
                                isEdit
                                    ? $t('Edit locked rate for {{project}}', { project: project.name })
                                    : $t('Lock rate for {{project}}', { project: project.name })
                            }
                            close={close}
                        />

                        <div className="modal__form-container">
                            <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                                {$t(
                                    '{{project}} is budgeted in {{currency}}. The locked rate replaces the {{currency}} common rate for this project only, until it is unlocked.',
                                    { project: project.name, currency: project.currency },
                                )}
                            </p>

                            <LockRateField label={$t('Rate (1 {{anchor}} = X {{currency}})', { anchor, currency: project.currency })} />

                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                <label htmlFor="as_at" className="text-input__label">
                                    {$t('As at')} *
                                </label>
                                <FormikDatePickerField name="as_at" className="text-input" popperPlacement="bottom-start" />
                                {touched.as_at && errors.as_at ? <div className="tw-text-red-500 tw-text-sm">{errors.as_at}</div> : null}
                            </div>

                            <TextFormField
                                label={$t('Reason')}
                                name="reason"
                                type="text"
                                placeholder={$t('e.g. hedged with bank, budget version 2 rate')}
                                required
                            />

                            {isRate(values.rate) && (
                                <p className="tw-mb-0">
                                    {$t('Check:')} <CheckFigure currency={project.currency} anchor={anchor} rate={values.rate} />
                                </p>
                            )}

                            <ModalFooter
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Lock rate')}
                                submitButtonVariant="primary"
                                onCancel={close}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    </>
                )}
            </Formik>
        </Modal>
    );
}
